import { ColumnDef } from '@tanstack/react-table';
import {
  CheckCircle,
  CreditCard,
  DollarSign,
  FileText,
  Search,
  Truck,
  User,
  Users,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { romaneioService } from '../../api';
import {
  DataTable,
  NoOpsSelected,
  PageError,
  PageLoading,
  RefreshButton,
} from '../../components';
import { createSortableHeader } from '../../components/DataTable';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { StatBox } from '../../components/ui/stat-box';
import { Tabs, TabsContent, PageTabsList, PageTabsTrigger } from '../../components/ui/tabs';
import { useOps } from '../../hooks/useOps';
import { useAppSelector } from '../../store';
import { formatCpfCnpj, formatCurrencyBrlFromCents, formatDateIso } from '../../utils';

// ─── Types ────────────────────────────────────────────────────────────────────

interface SaldoVencimento {
  data_vencimento: string;
  valor_total: number;
  total_registros: number;
}

interface DeliveryStatus {
  summary?: {
    acumuladoPedidos: number;
    acumuladoGarantido: number;
    semCadastro: boolean;
    telefoneIgual: boolean | null;
  };
  garantido?: any[];
  pedidos?: any[];
  cadastro?: any[];
  saldoVencimento?: SaldoVencimento[];
}

// ─── Columns ─────────────────────────────────────────────────────────────────

const listColumns: ColumnDef<any>[] = [
  {
    accessorKey: 'nome',
    header: createSortableHeader('Nome'),
    cell: ({ row }) => (
      <div className="font-medium text-[hsl(210,50%,10%)]">{row.getValue('nome')}</div>
    ),
  },
  {
    accessorKey: 'documento',
    header: 'CPF/CNPJ',
    size: 150,
  },
  {
    accessorKey: 'telefone',
    header: 'Telefone',
    size: 130,
  },
];

const cadastroColumns: ColumnDef<any>[] = [
  { accessorKey: 'telefone', header: 'Telefone' },
  { accessorKey: 'is_new', header: 'Cadastro Em Aguardo' },
];

const garantidoColumns: ColumnDef<any>[] = [
  { accessorKey: 'cnpj_cpf_entregador', header: 'Documento' },
  { accessorKey: 'telefone', header: 'Telefone' },
  {
    accessorKey: 'valor_garantido',
    header: 'Valor Garantido',
    cell: ({ getValue }) => formatCurrencyBrlFromCents(getValue() as number),
  },
  {
    accessorKey: 'data_romaneio',
    header: 'Data Romaneio',
    cell: ({ getValue }) => formatDateIso(getValue() as string) || '',
  },
];

const pedidosColumns: ColumnDef<any>[] = [
  { accessorKey: 'cnpj_cpf_entregador', header: 'Documento' },
  { accessorKey: 'taxa_total_entregador', header: 'Taxa Total Entregador',
    cell: ({ getValue }) => formatCurrencyBrlFromCents(getValue() as number) },
  { accessorKey: 'data_romaneio', header: 'Data Romaneio',
    cell: ({ getValue }) => formatDateIso(getValue() as string) || '' },
];

const saldoVencimentoColumns: ColumnDef<SaldoVencimento>[] = [
  {
    accessorKey: 'data_vencimento',
    header: createSortableHeader('Vencimento'),
    cell: ({ getValue }) => formatDateIso(getValue() as string) || '',
  },
  {
    accessorKey: 'valor_total',
    header: 'Valor Total',
    cell: ({ getValue }) => formatCurrencyBrlFromCents(getValue() as number),
  },
  { accessorKey: 'total_registros', header: 'Registros' },
];

// ─── Summary status badge ─────────────────────────────────────────────────────

function StatusBadge({ ok, labelTrue, labelFalse }: { ok: boolean; labelTrue: string; labelFalse: string }) {
  return (
    <Badge
      variant={ok ? 'default' : 'destructive'}
      className={ok
        ? 'bg-[hsl(142,76%,36%)]/10 text-[hsl(142,76%,36%)] border border-[hsl(142,76%,36%)]/20 hover:bg-[hsl(142,76%,36%)]/20'
        : 'bg-red-50 text-red-700 border border-red-200 hover:bg-red-100'}
    >
      {ok ? labelTrue : labelFalse}
    </Badge>
  );
}

// ─── Summary cards ────────────────────────────────────────────────────────────

function SummaryMetric({
  label,
  value,
  icon,
  color,
}: {
  label: string;
  value: React.ReactNode;
  icon: React.ReactNode;
  color: string;
}) {
  return (
    <Card className="bg-white border-[hsl(214,32%,91%)] shadow-sm">
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-[hsl(215,16%,47%)] mb-1 uppercase tracking-wide">{label}</p>
            <div className={`text-2xl font-bold ${color}`}>{value}</div>
          </div>
          <div className={`${color} opacity-80`}>{icon}</div>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

export function RomaneioEntregadores() {
  const { currentOps, getCurrentOpsConfig } = useOps();
  const { opsConfigs } = useAppSelector((state) => state.ops);

  // ── Lista state ──────────────────────────────────────────────────────────
  const [listData, setListData] = useState<any[]>([]);
  const [totalAntecipado, setTotalAntecipado] = useState<number>(0);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<string>('');

  // ── Checagem state ───────────────────────────────────────────────────────
  const [documento, setDocumento] = useState<string>('');
  const [deliveryStatus, setDeliveryStatus] = useState<DeliveryStatus | null>(null);
  const [checagemLoading, setChecagemLoading] = useState(false);
  const [checagemError, setChecagemError] = useState<string>('');
  const [searchPerformed, setSearchPerformed] = useState(false);

  // ── Load list ────────────────────────────────────────────────────────────

  const loadList = useCallback(async () => {
    if (!currentOps) return;
    const opsConfig = getCurrentOpsConfig();
    const dbName = opsConfig?.dbName;
    if (!dbName) return;

    setListLoading(true);
    setListError('');

    try {
      const response = await romaneioService.getCadastroView({ dbName, ops: currentOps });

      let totalAntecipadoValue = 0;
      if (response.data?.totalAntecipado?.length > 0) {
        const raw = response.data.totalAntecipado[0]?.total_valor_compra;
        const cents = typeof raw === 'string' ? parseInt(raw, 10) : raw || 0;
        totalAntecipadoValue = cents / 100;
      }
      setTotalAntecipado(totalAntecipadoValue);

      let dataArray: any[] = [];
      if (response.data) {
        if (response.data.cadastro && Array.isArray(response.data.cadastro)) {
          dataArray = response.data.cadastro;
        } else if (response.data.data?.cadastro && Array.isArray(response.data.data.cadastro)) {
          dataArray = response.data.data.cadastro;
        } else if (Array.isArray(response.data)) {
          dataArray = response.data;
        } else if (response.data.cadastros && Array.isArray(response.data.cadastros)) {
          dataArray = response.data.cadastros;
        }
      }

      setListData(
        dataArray.map((item: any, index: number) => ({
          id: index + 1,
          nome: item.nome_completo || item.nome || 'N/A',
          documento: item.numero_documento || item.cpf || item.cnpj || 'N/A',
          telefone: item.telefone || 'N/A',
          ...item,
        }))
      );
    } catch (err: any) {
      setListError(err.message || 'Erro ao carregar dados de cadastro');
    } finally {
      setListLoading(false);
    }
  }, [currentOps]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!currentOps) return;
    const opsConfig = getCurrentOpsConfig();
    if (!opsConfig?.dbName) return;

    const controller = new AbortController();
    let mounted = true;

    (async () => {
      setListLoading(true);
      setListError('');
      try {
        const response = await romaneioService.getCadastroView({
          dbName: opsConfig.dbName as string,
          ops: currentOps,
        });
        if (controller.signal.aborted || !mounted) return;

        let totalAntecipadoValue = 0;
        if (response.data?.totalAntecipado?.length > 0) {
          const raw = response.data.totalAntecipado[0]?.total_valor_compra;
          const cents = typeof raw === 'string' ? parseInt(raw, 10) : raw || 0;
          totalAntecipadoValue = cents / 100;
        }
        setTotalAntecipado(totalAntecipadoValue);

        let dataArray: any[] = [];
        if (response.data) {
          if (response.data.cadastro && Array.isArray(response.data.cadastro)) {
            dataArray = response.data.cadastro;
          } else if (response.data.data?.cadastro && Array.isArray(response.data.data.cadastro)) {
            dataArray = response.data.data.cadastro;
          } else if (Array.isArray(response.data)) {
            dataArray = response.data;
          } else if (response.data.cadastros && Array.isArray(response.data.cadastros)) {
            dataArray = response.data.cadastros;
          }
        }
        setListData(
          dataArray.map((item: any, index: number) => ({
            id: index + 1,
            nome: item.nome_completo || item.nome || 'N/A',
            documento: item.numero_documento || item.cpf || item.cnpj || 'N/A',
            telefone: item.telefone || 'N/A',
            ...item,
          }))
        );
      } catch (err: any) {
        if (controller.signal.aborted || !mounted) return;
        setListError(err.message || 'Erro ao carregar dados de cadastro');
      } finally {
        if (!controller.signal.aborted && mounted) setListLoading(false);
      }
    })();

    return () => {
      mounted = false;
      controller.abort();
    };
  }, [currentOps, opsConfigs]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Checagem ─────────────────────────────────────────────────────────────

  const handleSearch = async () => {
    if (!currentOps || !documento.trim()) return;
    const opsConfig = getCurrentOpsConfig();
    const dbName = opsConfig?.dbName;
    if (!dbName) {
      setChecagemError('Configuração de banco de dados não encontrada. Aguarde...');
      return;
    }

    setChecagemLoading(true);
    setChecagemError('');
    setDeliveryStatus(null);

    const digits = documento.replace(/\D/g, '');
    if (digits.length !== 11 && digits.length !== 14) {
      setChecagemError('Digite um CPF (11 dígitos) ou CNPJ (14 dígitos) válido');
      setChecagemLoading(false);
      return;
    }

    try {
      const response = await romaneioService.getStatusPerson({
        numeroDocumento: digits,
        dbName,
        ops: currentOps,
      });

      if (response.data && response.data !== 0) {
        setDeliveryStatus(response.data);
      } else {
        setChecagemError('Usuário não encontrado para o documento informado');
      }
      setSearchPerformed(true);
    } catch (err: any) {
      setChecagemError(err.message || 'Erro ao consultar status do usuário');
      setSearchPerformed(true);
    } finally {
      setChecagemLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const clearSearch = () => {
    setDocumento('');
    setDeliveryStatus(null);
    setChecagemError('');
    setSearchPerformed(false);
  };

  // ── Guard ─────────────────────────────────────────────────────────────────

  if (!currentOps) {
    return <NoOpsSelected message="Selecione uma operação para acessar os usuários." />;
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-[hsl(210,50%,10%)]">
            Usuários
          </h1>
          <p className="text-[hsl(215,16%,47%)]">
            Gerencie a lista geral e consulte usuários individualmente
          </p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="lista" className="w-full">
        <PageTabsList>
          <PageTabsTrigger value="lista">
            <Users className="h-4 w-4" />
            Lista
          </PageTabsTrigger>
          <PageTabsTrigger value="checagem">
            <Search className="h-4 w-4" />
            Checagem Individual
          </PageTabsTrigger>
        </PageTabsList>

        {/* ── Tab: Lista ─────────────────────────────────────────────────── */}
        <TabsContent value="lista" className="mt-0 space-y-5">
          <div className="flex justify-end">
            <RefreshButton onClick={loadList} loading={listLoading} />
          </div>

          <PageError error={listError} />
          <PageLoading loading={listLoading} message="Carregando lista de usuários..." />

          {!listLoading && (
            <>
              <div className="grid gap-4 md:grid-cols-2">
                <StatBox
                  title="Total Antecipado"
                  value={totalAntecipado}
                  valueType="currency"
                  icon={<DollarSign className="h-6 w-6 text-[hsl(142,76%,36%)]" />}
                />
                <StatBox
                  title="Total de Usuários"
                  value={listData.length}
                  valueType="integer"
                  icon={<Users className="h-6 w-6 text-[hsl(210,35%,32%)]" />}
                />
              </div>

              <DataTable
                data={listData}
                columns={listColumns}
                title="Usuários Cadastrados"
                loading={false}
                onRefresh={loadList}
                height={600}
                searchPlaceholder="Buscar por nome, documento ou telefone..."
                pageSize={100}
              />
            </>
          )}
        </TabsContent>

        {/* ── Tab: Checagem ──────────────────────────────────────────────── */}
        <TabsContent value="checagem" className="mt-0 space-y-5">

          {/* Search card */}
          <Card className="bg-white border-[hsl(214,32%,91%)] shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold text-[hsl(210,50%,10%)]">
                Consulta por Documento
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="flex gap-3 items-end">
                <div className="flex-1">
                  <Label htmlFor="documento" className="text-[hsl(210,50%,10%)] font-medium text-sm mb-1.5 block">
                    CPF ou CNPJ do Usuário
                  </Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[hsl(215,16%,47%)]" />
                    <Input
                      id="documento"
                      value={documento}
                      onChange={(e) => setDocumento(formatCpfCnpj(e.target.value))}
                      onKeyDown={handleKeyDown}
                      placeholder="000.000.000-00 ou 00.000.000/0000-00"
                      maxLength={18}
                      className="pl-10 h-10 bg-[hsl(210,40%,98%)] border-[hsl(214,32%,91%)] focus:border-[hsl(43,56%,61%)] focus:ring-[hsl(43,56%,61%)]"
                    />
                  </div>
                  {(() => {
                    const digits = documento.replace(/\D/g, '');
                    if (digits.length === 0 || digits.length === 11 || digits.length === 14) return null;
                    return (
                      <p className="mt-1.5 text-xs text-[hsl(215,16%,47%)]">
                        CPF: 11 dígitos&nbsp;&nbsp;·&nbsp;&nbsp;CNPJ: 14 dígitos
                        {digits.length > 0 && digits.length < 14 && (
                          <span className="ml-1 text-amber-600">
                            ({digits.length} digitado{digits.length > 1 ? 's' : ''})
                          </span>
                        )}
                      </p>
                    );
                  })()}
                </div>
                <Button
                  onClick={handleSearch}
                  disabled={checagemLoading || ![11, 14].includes(documento.replace(/\D/g, '').length)}
                  className="h-10 px-5 bg-[hsl(210,35%,32%)] hover:bg-[hsl(210,35%,25%)] text-white"
                >
                  {checagemLoading ? (
                    <>
                      <div className="animate-spin mr-2 h-4 w-4 border-2 border-current border-t-transparent rounded-full" />
                      Buscando...
                    </>
                  ) : (
                    <>
                      <Search className="h-4 w-4 mr-2" />
                      Buscar
                    </>
                  )}
                </Button>
                {(deliveryStatus || checagemError) && (
                  <Button variant="outline" onClick={clearSearch} className="h-10">
                    Limpar
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          <PageError error={checagemError} />
          <PageLoading loading={checagemLoading} message="Consultando status do usuário..." />

          {/* Results */}
          {deliveryStatus && !checagemLoading && (
            <div className="space-y-6">

              {/* InDrive: saldo agrupado por vencimento */}
              {currentOps === 'indrive' ? (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <SummaryMetric
                      label="Total Bônus"
                      value={formatCurrencyBrlFromCents(
                        (deliveryStatus.saldoVencimento || []).reduce((acc, r) => acc + Number(r.valor_total), 0)
                      )}
                      icon={<DollarSign className="h-7 w-7" />}
                      color="text-[hsl(210,35%,32%)]"
                    />
                    <SummaryMetric
                      label="Vencimentos"
                      value={(deliveryStatus.saldoVencimento || []).length}
                      icon={<FileText className="h-7 w-7" />}
                      color="text-[hsl(230,50%,50%)]"
                    />
                  </div>

                  <Card className="bg-white border-[hsl(214,32%,91%)] shadow-sm">
                    <CardHeader className="pb-3">
                      <div className="flex items-center gap-2">
                        <CreditCard className="h-4 w-4 text-[hsl(210,35%,32%)]" />
                        <CardTitle className="text-sm font-semibold text-[hsl(210,50%,10%)]">
                          Saldo por Vencimento
                        </CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      {(deliveryStatus.saldoVencimento || []).length === 0 ? (
                        <p className="text-center text-[hsl(215,16%,47%)] py-8">Nenhum bônus encontrado para este usuário.</p>
                      ) : (
                        <DataTable
                          columns={saldoVencimentoColumns}
                          data={deliveryStatus.saldoVencimento || []}
                          searchPlaceholder="Buscar por vencimento..."
                        />
                      )}
                    </CardContent>
                  </Card>
                </>
              ) : (
                <>
                  {/* Quick status pills */}
                  <div className="flex flex-wrap gap-2 items-center">
                    <span className="text-sm font-medium text-[hsl(215,16%,47%)]">Status rápido:</span>
                    <StatusBadge
                      ok={!deliveryStatus.summary?.semCadastro}
                      labelTrue="Com Cadastro"
                      labelFalse="Sem Cadastro"
                    />
                    <StatusBadge
                      ok={(deliveryStatus.garantido?.length || 0) > 0}
                      labelTrue="Com Garantido"
                      labelFalse="Sem Garantido"
                    />
                    <StatusBadge
                      ok={(deliveryStatus.pedidos?.length || 0) > 0}
                      labelTrue="Com Corridas"
                      labelFalse="Sem Corridas"
                    />
                    {deliveryStatus.summary?.telefoneIgual !== null && (
                      <StatusBadge
                        ok={deliveryStatus.summary?.telefoneIgual === true}
                        labelTrue="Telefone Igual"
                        labelFalse="Telefone Divergente"
                      />
                    )}
                  </div>

                  {/* Metric cards */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <SummaryMetric
                      label="Valor Face Pedidos"
                      value={formatCurrencyBrlFromCents(deliveryStatus.summary?.acumuladoPedidos || 0)}
                      icon={<DollarSign className="h-7 w-7" />}
                      color="text-[hsl(210,35%,32%)]"
                    />
                    <SummaryMetric
                      label="Valor Face Garantido"
                      value={formatCurrencyBrlFromCents(deliveryStatus.summary?.acumuladoGarantido || 0)}
                      icon={<CreditCard className="h-7 w-7" />}
                      color="text-[hsl(142,76%,36%)]"
                    />
                    <SummaryMetric
                      label="Registros Pedidos"
                      value={deliveryStatus.pedidos?.length || 0}
                      icon={<Truck className="h-7 w-7" />}
                      color="text-[hsl(230,50%,50%)]"
                    />
                    <SummaryMetric
                      label="Registros Garantido"
                      value={deliveryStatus.garantido?.length || 0}
                      icon={<CheckCircle className="h-7 w-7" />}
                      color="text-[hsl(43,56%,50%)]"
                    />
                  </div>

                  {/* Detail tables */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {deliveryStatus.cadastro && deliveryStatus.cadastro.length > 0 && (
                      <Card className="bg-white border-[hsl(214,32%,91%)] shadow-sm">
                        <CardHeader className="pb-3">
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-[hsl(270,50%,50%)]" />
                            <CardTitle className="text-sm font-semibold text-[hsl(210,50%,10%)]">
                              Dados de Cadastro
                            </CardTitle>
                          </div>
                        </CardHeader>
                        <CardContent className="pt-0">
                          <DataTable
                            columns={cadastroColumns}
                            data={deliveryStatus.cadastro}
                            searchPlaceholder="Buscar por telefone..."
                          />
                        </CardContent>
                      </Card>
                    )}

                    {deliveryStatus.garantido && deliveryStatus.garantido.length > 0 && (
                      <Card className="bg-white border-[hsl(214,32%,91%)] shadow-sm">
                        <CardHeader className="pb-3">
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-[hsl(43,56%,50%)]" />
                            <CardTitle className="text-sm font-semibold text-[hsl(210,50%,10%)]">
                              Dados Garantido
                            </CardTitle>
                          </div>
                        </CardHeader>
                        <CardContent className="pt-0">
                          <DataTable
                            columns={garantidoColumns}
                            data={deliveryStatus.garantido}
                            searchPlaceholder="Buscar por documento..."
                          />
                        </CardContent>
                      </Card>
                    )}

                    {deliveryStatus.pedidos && deliveryStatus.pedidos.length > 0 && (
                      <Card className="col-span-full bg-white border-[hsl(214,32%,91%)] shadow-sm">
                        <CardHeader className="pb-3">
                          <div className="flex items-center gap-2">
                            <Truck className="h-4 w-4 text-[hsl(230,50%,50%)]" />
                            <CardTitle className="text-sm font-semibold text-[hsl(210,50%,10%)]">
                              Histórico de Pedidos
                            </CardTitle>
                          </div>
                        </CardHeader>
                        <CardContent className="pt-0">
                          <DataTable
                            columns={pedidosColumns}
                            data={deliveryStatus.pedidos}
                            searchPlaceholder="Buscar por documento..."
                          />
                        </CardContent>
                      </Card>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Empty state */}
          {searchPerformed && !deliveryStatus && !checagemLoading && !checagemError && (
            <Card className="bg-white border-[hsl(214,32%,91%)] shadow-sm">
              <CardContent className="text-center py-12">
                <User className="h-10 w-10 text-[hsl(215,16%,47%)] mx-auto mb-3" />
                <h3 className="text-base font-medium text-[hsl(210,50%,10%)] mb-1">
                  Nenhum resultado encontrado
                </h3>
                <p className="text-sm text-[hsl(215,16%,47%)]">
                  Verifique se o CPF/CNPJ foi digitado corretamente e tente novamente.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
